import * as Popover from "@radix-ui/react-popover";
import classNames from "./TimeRangeSelect.module.css";
import DatePicker from "react-datepicker";
import Button from "../../../common/Button";
import { IoChevronDown } from "react-icons/io5";
import { useCallback, useEffect, useRef, useState } from "react";

export type TimeRange = {
  name:
    | "last-24-hours"
    | "last-month"
    | "last-3-months"
    | "last-12-months"
    | "all-time"
    | "custom";
  start?: Date;
  end?: Date;
};

// Add 5 minute padding to get real-time data
const defaultTimeRanges: TimeRange[] = [
  // Last 24 hours
  {
    name: "last-24-hours",
    start: new Date(new Date().setHours(new Date().getHours() - 24)),
    end: new Date(new Date().setMinutes(new Date().getMinutes() + 5)),
  },
  // Last month
  {
    name: "last-month",
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    end: new Date(new Date().setMinutes(new Date().getMinutes() + 5)),
  },
  {
    name: "last-3-months",
    start: new Date(new Date().setMonth(new Date().getMonth() - 2)),
    end: new Date(new Date().setMinutes(new Date().getMinutes() + 5)),
  },
  {
    name: "last-12-months",
    start: new Date(new Date().setMonth(new Date().getMonth() - 12)),
    end: new Date(new Date().setMinutes(new Date().getMinutes() + 5)),
  },
  // All Time
  {
    name: "all-time",
    start: new Date("2025-01-01"),
    end: undefined,
  },
];

const TimeRangeSelect = (props: {
  selectedTimeRange: TimeRange;
  onChange: (value: TimeRange) => void;
}) => {
  const getTimeRangeName = useCallback((range: TimeRange["name"]) => {
    switch (range) {
      case "last-24-hours":
        return "Today";
      case "last-month":
        return "Last month";
      case "last-3-months":
        return "Last 3 months";
      case "last-12-months":
        return "Last 12 months";
      case "all-time":
        return "All time";
      case "custom":
        return "Customize";
    }
  }, []);

  const [showCustom, setShowCustom] = useState(
    props.selectedTimeRange.name === "custom"
  );

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>();

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, []);

  return (
    <Popover.Root>
      <Popover.Trigger asChild ref={triggerRef}>
        <Button
          color="gray"
          backgroundColor="black"
          borderColor="dark-gray"
          className={classNames.Trigger}
        >
          {getTimeRangeName(props.selectedTimeRange.name)}
          <IoChevronDown />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={classNames.TimeRangeSelectContent}
          align="start"
          side="bottom"
          sideOffset={8}
          style={{ minWidth: triggerWidth }}
        >
          <div className={classNames.DefaultTimeRanges}>
            {defaultTimeRanges.map((timeRange) => (
              <button
                type="button"
                key={timeRange.name}
                onClick={() => props.onChange(timeRange)}
                aria-selected={props.selectedTimeRange.name === timeRange.name}
              >
                {getTimeRangeName(timeRange.name)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowCustom((prev) => !prev)}
              aria-selected={props.selectedTimeRange.name === "custom"}
            >
              Customize
            </button>
          </div>
          {showCustom && (
            <div className={classNames.CustomTimeRange}>
              <div className={classNames.DatePicker}>
                <label>From</label>
                <DatePicker
                  selected={props.selectedTimeRange.start}
                  onChange={(date) =>
                    props.onChange({
                      ...props.selectedTimeRange,
                      name: "custom",
                      start: date || undefined,
                    })
                  }
                />
              </div>
              <div className={classNames.DatePicker}>
                <label>To</label>
                <DatePicker
                  selected={props.selectedTimeRange.end}
                  onChange={(date) =>
                    props.onChange({
                      ...props.selectedTimeRange,
                      name: "custom",
                      end: date || undefined,
                    })
                  }
                />
              </div>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default TimeRangeSelect;
